import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Shield, Truck, DollarSign, Clock, HeadphonesIcon, BadgeCheck } from 'lucide-react';

const features = [
  {
    icon: Shield,
    title: "100% Authentic Products",
    description: "All products sourced directly from authorized brand distributors with genuine warranty"
  },
  {
    icon: Truck,
    title: "Global Logistics",
    description: "Seamless shipping and delivery across continents with real-time tracking"
  },
  {
    icon: DollarSign,
    title: "Competitive B2B Pricing",
    description: "Best wholesale rates for bulk orders with flexible payment terms"
  },
  {
    icon: Clock,
    title: "Quick Turnaround",
    description: "Fast order processing and dispatch with efficient supply chain management"
  },
  {
    icon: HeadphonesIcon,
    title: "24/7 Support",
    description: "Dedicated account managers and round-the-clock customer support"
  },
  {
    icon: BadgeCheck,
    title: "Trusted Partner",
    description: "7+ years of proven track record serving businesses worldwide"
  }
];

const WhyChooseUs = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="why-us" className="py-20 md:py-32 relative overflow-hidden" data-testid="why-choose-section">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-brand-accent/20 text-brand-accent text-sm font-medium mb-4">
            Why Choose Us
          </span>
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            The <span className="gradient-text">AJKTV Advantage</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Partner with us for reliable, efficient, and cost-effective B2B electronics solutions
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="group p-6 md:p-8 rounded-2xl bg-brand-surface/50 border border-white/5 hover:border-brand-primary/30 transition-all duration-300"
              data-testid={`feature-card-${index}`}
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-primary/20 to-brand-accent/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <feature.icon className="w-7 h-7 text-brand-accent" />
              </div>
              
              <h3 className="font-outfit text-xl font-semibold text-white mb-3">
                {feature.title}
              </h3>
              
              <p className="text-gray-400 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center gap-4 px-6 py-4 rounded-2xl glass">
            <span className="text-gray-300">Ready to partner with us?</span>
            <a 
              href="#contact"
              className="px-6 py-2 rounded-full bg-brand-accent text-brand-dark font-semibold hover:bg-brand-accent/90 transition-colors"
              data-testid="why-us-cta"
            >
              Get Started
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
